<script>
	import { deleteDoc, doc, getFirestore, setDoc } from "firebase/firestore";
	import {
		flattenObject,
		convertObjectToText,
		convertTextToObject,
		sortKeys,
		unflattenObject,
		watchObject,
	} from "cando-util";
	import {
		faCircleCheck,
		faTrashCan,
		faCheck,
		faPen,
		faXmark,
		faRotateLeft,
		faSave,
	} from "@fortawesome/free-solid-svg-icons";

	import { store } from "$lib/utilities/store";
	import { callAiResponse } from "./recordUpdate.js";

	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import RecordGroups from "./RecordGroups.svelte";
	import Loading from "$lib/components/Loading.svelte";

	export let col;
	export let docId;
	export let onClose = () => {};
	export let tableFields;
	export let allowDelete;
	export let allowFormEdit = true;
	export let formDefaultsSettingId;

	$: watchObject(callAiResponse, "callAiResponse");

	$: docs = $store?.[col]?.docs || {};
	$: record = docs?.[docId];
	$: watchObject(record, "record");
	$: watchObject(docId, "docId");

	// $: pendingAgents = ["Test"];

	$: pendingAgents = Object.entries(
		$store?.[col]?.docs?.[docId]?.aiResponses || {},
	)
		.filter(([k, v]) => v.completed === "pending...")
		.map(([k, v]) => k);

	let editMode = false;

	const toggleEdit = () => {
		editMode = !editMode;
	};

	const handleClose = () => {
		editMode = false;
		onClose();
	};

	$: docTitle =
		$store?.[col]?.docs?.[docId] &&
		(tableFields?.find((f) => f.isDocHeading)?.function(docId) ||
			tableFields?.find((f) => f.isIdentifier)?.function(docId));

	$: layoutClass =
		$store?.userProfile?.collectionCriteria?.[col]?.formLayoutClass ||
		"p-4 space-y-4 max-h-[48rem] overflow-y-auto";

	const setDefault = async () => {
		if (
			typeof formDefaultsSettingId !== "string" ||
			!formDefaultsSettingId.trim()
		)
			return;

		const newDefaultFormLayout = flattenObject(
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || [],
			col,
			true,
			true,
		);
		const newDefaultFormLayoutClass = {
			[col]: $store.userProfile?.collectionCriteria?.[col]
				?.formLayoutClass,
		};

		const existingFormLayouts = unflattenObject(
			convertTextToObject(
				$store.settings?.docs?.[formDefaultsSettingId]?.defaultFormLayouts
					?.value || "",
			),
		);
		delete existingFormLayouts[col];
		const cleanExistingFormLayouts = flattenObject(
			existingFormLayouts,
			"",
			true,
			true,
		);

		const existingLayoutClasses = convertTextToObject(
			$store.settings?.docs?.[formDefaultsSettingId]
				?.defaultFormLayoutClasses
				?.value || "",
		);
		const newFormLayouts = sortKeys({
			...cleanExistingFormLayouts,
			...newDefaultFormLayout,
		});
		const newLayoutClasses = sortKeys({
			...existingLayoutClasses,
			...newDefaultFormLayoutClass,
		});

		const updates = {
			defaultFormLayouts: {
				type: "TextArea",
				permission: "Admin",
				...$store.settings?.docs?.[formDefaultsSettingId]
					?.defaultFormLayouts,
				value: convertObjectToText(newFormLayouts),
			},
			defaultFormLayoutClasses: {
				type: "TextArea",
				permission: "Admin",
				...$store.settings?.docs?.[formDefaultsSettingId]
					?.defaultFormLayoutClasses,
				value: convertObjectToText(newLayoutClasses),
			},
		};

		await setDoc(
			doc(getFirestore(), "settings", formDefaultsSettingId),
			updates,
			{ merge: true },
		);
	};

	$: defaultFormLayouts =
		$store.settings?.docs?.[formDefaultsSettingId]?.defaultFormLayouts
			?.value ||
		"{}";
	$: watchObject(defaultFormLayouts, "defaultFormLayouts");
	$: defaultFormLayoutClasses =
		$store.settings?.docs?.[formDefaultsSettingId]?.defaultFormLayoutClasses
			?.value || "{}";
	$: watchObject(defaultFormLayoutClasses, "defaultFormLayoutClasses");
	$: if (
		allowFormEdit === false &&
		!!defaultFormLayouts &&
		!!defaultFormLayoutClasses
	) {
		getDefault();
	}

	const getDefault = async () => {
		const defaultLayout = unflattenObject(
			sortKeys(convertTextToObject(defaultFormLayouts) || {}),
		)?.[col];
		const defaultLayoutClass = convertTextToObject(
			defaultFormLayoutClasses,
		)[col];

		if (!Array.isArray(defaultLayout)) return;
		if (typeof defaultLayoutClass !== "string") return;

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		const updates = {
			collectionCriteria: {
				[col]: {
					...($store.userProfile?.collectionCriteria?.[col] || {}),
					formLayout: defaultLayout,
					formLayoutClass: defaultLayoutClass,
				},
			},
		};

		await setDoc(profileDocRef, updates, { merge: true });
	};

	let loading = true;
	let deleteConfirm = false;
	const deleteFunction = async () => {
		if (!deleteConfirm) {
			deleteConfirm = true;
			setTimeout(() => {
				deleteConfirm = false;
			}, 5000);
			return;
		}
		loading = true;

		const currentStore = $store?.[col];
		delete currentStore.docs[docId];
		store.set({
			...$store,
			[col]: {
				...currentStore,
			},
		});

		await deleteDoc(doc(getFirestore(), `${col}/${docId}`));

		loading = false;

		handleClose();

		deleteConfirm = false;
		console.log("delete function");
	};

	const updateFormLayoutClass = async (v) => {
		await setDoc(
			doc(getFirestore(), "profiles", $store.userProfile.uid),
			{ collectionCriteria: { [col]: { formLayoutClass: v } } },
			{ merge: true },
		);
	};
</script>

<section class="mt-8 border border-primary rounded bg-base-200">
	<header
		class="flex flex-wrap gap-2 items-start justify-between border-b border-primary p-4"
	>
		<div class="space-y-2">
			<p class="uppercase tracking-wide text-xs text-base-content/60">
				{col}
			</p>
			<h3 class="text-xl font-semibold break-all">
				{@html docTitle}
			</h3>
		</div>
		<div class="flex gap-2">
			{#each pendingAgents as agent}
				<Loading label={`${agent} AI Response Pending...`} />
			{/each}

			{#if allowFormEdit}
				<button
					class="btn btn-sm gap-2"
					type="button"
					on:click={toggleEdit}
					title={editMode ? "Done" : "Edit"}
				>
					<SvelteFa icon={editMode ? faCheck : faPen} />
				</button>
			{/if}
			{#if editMode && allowFormEdit}
				<input
					type="text"
					class="input input-bordered input-primary input-sm"
					placeholder="Layout class"
					value={layoutClass}
					on:change={(event) =>
						updateFormLayoutClass(event.currentTarget.value)}
				/>
				<button
					class="btn btn-sm gap-2"
					type="button"
					on:click={getDefault}
					title="Load Default Layout"
				>
					<SvelteFa icon={faRotateLeft} size="1x" />
				</button>
				{#if $store.userRoles?.admin}
					<button
						class="btn btn-sm gap-2"
						type="button"
						on:click={setDefault}
						title="Use As Default Layout"
					>
						<SvelteFa icon={faSave} size="1x" />
					</button>
				{/if}
			{/if}
			<button
				class="btn btn-sm btn-ghost gap-2"
				type="button"
				on:click={handleClose}
				title="Close"
			>
				<SvelteFa icon={faXmark} />
			</button>
		</div>
	</header>

	{#if record}
		<RecordGroups {col} {docId} {editMode} {tableFields} {layoutClass} />
	{:else}
		<div class="p-6 text-center text-base-content/70">
			Unable to locate that record in the {col} collection.
		</div>
	{/if}

	{#if allowDelete === true}
		<div class="flex justify-end m-2 sm:m-4">
			<button
				class={`btn btn-sm ${deleteConfirm ? "btn-error animate-pulse" : "btn-warning"}`}
				on:click|preventDefault={deleteFunction}
			>
				{#if deleteConfirm}
					<div class={`flex ${loading ? "loading" : ""}`}>
						<SvelteFa icon={faCircleCheck} size="1.5x" />
					</div>
					<span class=""> Confirm </span>
				{:else}
					<SvelteFa icon={faTrashCan} size="1.5x" />
					<span class=""> Delete </span>
				{/if}
			</button>
		</div>
	{/if}
</section>
